import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import { getCorrelation, getNutsInventory } from "../../services/recommendationsService";
import CorrelationResults from "./CorrelationResults";
import {
  DATASET_CATEGORIES,
  CORRELATION_VARIABLES_BY_CATEGORY,
  nutsLabel,
  CURRENT_YEAR,
} from "./FormConfig";

export default function CorrelationTab() {
  // Filters
  const [category, setCategory] = useState(DATASET_CATEGORIES[0].value);
  const [nutsLevel, setNutsLevel] = useState(1);
  const [yearFrom, setYearFrom] = useState(2006);
  const [yearTo, setYearTo] = useState(2019);
  const [region, setRegion] = useState("");
  const [exposureVariable, setExposureVariable] = useState(
    CORRELATION_VARIABLES_BY_CATEGORY[DATASET_CATEGORIES[0].value][0].value
  );
  const [outcomeVariable, setOutcomeVariable] = useState(
    CORRELATION_VARIABLES_BY_CATEGORY[DATASET_CATEGORIES[0].value][1]?.value ||
      CORRELATION_VARIABLES_BY_CATEGORY[DATASET_CATEGORIES[0].value][0].value
  );

  // Data + UI state
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  // Dynamic regions state
  const [regionsData, setRegionsData] = useState(null);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regionsError, setRegionsError] = useState("");

  // Fetch NUTS inventory when category or yearTo changes
  const fetchRegions = useCallback(async () => {
    const categoryLabel =
      DATASET_CATEGORIES.find((c) => c.value === category)?.label || "";

    if (!categoryLabel || !yearTo) return;

    setRegionsLoading(true);
    setRegionsError("");

    try {
      const data = await getNutsInventory(yearTo, categoryLabel);
      setRegionsData(data);
    } catch (err) {
      setRegionsError(err.message || "Failed to load regions");
      setRegionsData(null);
    } finally {
      setRegionsLoading(false);
    }
  }, [category, yearTo]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // Derived options
  const variableOptions = useMemo(() => {
    const opts = CORRELATION_VARIABLES_BY_CATEGORY[category] || [];
    if (!opts.find((o) => o.value === exposureVariable) && opts.length) {
      setExposureVariable(opts[0].value);
    }
    if (!opts.find((o) => o.value === outcomeVariable) && opts.length > 1) {
      setOutcomeVariable(opts[1]?.value || opts[0].value);
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Build region options from API response
  const regionOptions = useMemo(() => {
    if (!regionsData || !Array.isArray(regionsData) || regionsData.length === 0) {
      return [];
    }

    // Aggregate regions from all datasets for the selected NUTS level
    const nutsKey = `NUTS${nutsLevel}`;
    const regionSet = new Set();

    regionsData.forEach((dataset) => {
      const regions = dataset.nuts?.[nutsKey] || [];
      regions.forEach((r) => regionSet.add(r));
    });

    // Convert to sorted array of options (value and label are the same - region name)
    const opts = Array.from(regionSet)
      .sort()
      .map((name) => ({ value: name, label: name }));

    // Reset region selection if current selection is not in the new options
    if (opts.length > 0 && !opts.find((o) => o.value === region)) {
      setRegion(opts[0].value);
    }

    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionsData, nutsLevel]);

  // Helpers
  const handleYearFrom = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(2000, Math.min(n, CURRENT_YEAR));
    setYearFrom(clamped);
    if (clamped > yearTo) setYearTo(clamped);
  };

  const handleYearTo = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(2000, Math.min(n, CURRENT_YEAR));
    setYearTo(clamped);
    if (clamped < yearFrom) setYearFrom(clamped);
  };

  const fetchCorrelation = async () => {
    setErrorMsg("");
    setResult(null);
    setLoading(true);

    try {
      const categoryLabel =
        DATASET_CATEGORIES.find((c) => c.value === category)?.label || "";

      const body = {
        category: categoryLabel,
        fromYear: yearFrom,
        toYear: yearTo,
        region: region, // Region is already the name from API
        nutsLevel: nutsLabel(nutsLevel),
        exposureVariable: exposureVariable, // Send label (value = label for correlation vars)
        outcomeVariable: outcomeVariable,
      };

      console.log("Correlation request body:", body);

      const data = await getCorrelation(body);
      setResult(data);
    } catch (err) {
      setErrorMsg(err.message || "Failed to fetch correlation analysis.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h4 className="chartHeader mb-4">
        Select parameters for correlation analysis
      </h4>

      <Row className="align-items-end g-3 mb-4">
        <Col xs={12} md={4} lg={3}>
          <Form.Label className="mb-1">Dataset Category</Form.Label>
          <Form.Select
            className="py-3"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {DATASET_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={6} md={2} lg={2}>
          <Form.Label className="mb-1">NUTS Level</Form.Label>
          <Form.Select
            className="py-3"
            value={nutsLevel}
            onChange={(e) => setNutsLevel(Number(e.target.value))}
          >
            <option value={1}>NUTS 1</option>
            <option value={2}>NUTS 2</option>
            <option value={3}>NUTS 3</option>
          </Form.Select>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Form.Label className="mb-1">
            Region{" "}
            {regionsLoading && (
              <Spinner animation="border" size="sm" className="ms-1" />
            )}
          </Form.Label>
          <Form.Select
            className="py-3"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            disabled={regionsLoading || regionOptions.length === 0}
          >
            {regionsLoading ? (
              <option value="">Loading regions...</option>
            ) : regionsError ? (
              <option value="">Error loading regions</option>
            ) : regionOptions.length === 0 ? (
              <option value="">No regions available</option>
            ) : (
              regionOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))
            )}
          </Form.Select>
        </Col>

        <Col xs={6} md={3} lg={2}>
          <Form.Label className="mb-1">From (Year)</Form.Label>
          <Form.Control
            type="number"
            min={2000}
            max={CURRENT_YEAR}
            value={yearFrom}
            onChange={(e) => handleYearFrom(e.target.value)}
          />
        </Col>

        <Col xs={6} md={3} lg={2}>
          <Form.Label className="mb-1">To (Year)</Form.Label>
          <Form.Control
            type="number"
            min={2000}
            max={CURRENT_YEAR}
            value={yearTo}
            onChange={(e) => handleYearTo(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="align-items-end g-3 mb-3">
        <Col xs={12} md={5}>
          <Form.Label className="mb-1">Exposure Variable</Form.Label>
          <Form.Select
            className="py-3"
            value={exposureVariable}
            onChange={(e) => {
              setExposureVariable(e.target.value);
              setResult(null); // Clear stale results when variable changes
            }}
          >
            {variableOptions.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} md={5}>
          <Form.Label className="mb-1">Outcome Variable</Form.Label>
          <Form.Select
            className="py-3"
            value={outcomeVariable}
            onChange={(e) => {
              setOutcomeVariable(e.target.value);
              setResult(null); // Clear stale results when variable changes
            }}
          >
            {variableOptions.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} md={2}>
          <Button
            className="w-100 py-3"
            variant="success"
            onClick={fetchCorrelation}
            disabled={loading || regionsLoading || !region}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Analyzing…
              </>
            ) : (
              "Analyze Correlation"
            )}
          </Button>
        </Col>
      </Row>

      {errorMsg && (
        <Alert variant="danger" className="mb-3">
          {errorMsg}
        </Alert>
      )}

      {result && (
        <CorrelationResults
          result={result}
          exposureVariable={exposureVariable}
          outcomeVariable={outcomeVariable}
        />
      )}
    </>
  );
}
